import { Game } from "@denishussar/html5-game-engine";
import { BaseRenderTexture, Container, extras, particles, RenderTexture, SCALE_MODES, Sprite, Text, Texture, WebGLRenderer } from "pixi.js";
import { GameLogic } from "../Logic/GameLogic";
import { GameModel } from "../Models/GameModel";
import { BufferRenderer } from "./BufferRenderer";

const chunkSize = 16; // result texture is 512px

interface IDesc {
    logic: GameLogic;
    context: WebGLRenderer;
    image: Texture;
    gridImage: Texture;
    pixelSize: number;
}

class Letters extends BufferRenderer {
    // container = new particles.ParticleContainer(chunkSize ** 2, { position: true }, chunkSize, true);
    static textures: RenderTexture[] = [];
    static bakeTextures(renderer: WebGLRenderer) {
        const fontSize = 48;
        const label = new PIXI.Text('', {
            fontSize: fontSize,
            fill: 0x000000,
            align: 'center',
        });
        GameModel.alphabetSymbol = GameModel.alphabetSymbolList.concat(Array.from({ length: GameModel.alphabetNumberMax }, (v, k) => (k + 1).toString()));
        GameModel.alphabetSymbol
            .forEach(symbol => {
                label.text = symbol.toString();
                label.scale.set(0.5);
                label.alpha = 0.5;
                // todo: generate 32px atlas
                Letters.textures.push(renderer.generateTexture(label));
            });
    }

    readonly colors = new Map<number, Sprite[]>();
    getLetters(color: number) {
        let a = this.colors.get(color);
        if (!a) {
            a = [] as Sprite[];
            this.colors.set(color, a);
        }
        return a;
    }

    setLetters(logic: GameLogic, image: Texture, chunkX: number, chunkY: number) {
        if (!Letters.textures.length) {
            Letters.bakeTextures(this.renderer);
        }
        let x = chunkX * chunkSize;
        for (let i = 0; i < chunkSize; i++) {
            if (x < image.width) {
                let y = chunkY * chunkSize;
                for (let ii = 0; ii < chunkSize; ii++) {
                    if (y < image.height) {
                        const pixelColor = logic.getPixelColor(x, y);
                        const indexColor = logic.getColorIndex(pixelColor);

                        const symbol = new Sprite(Letters.textures[indexColor - 1]);
                        symbol.anchor.set(0.25, 0.25);
                        symbol.position.set(
                            (i + 0.5) * GameModel.pixelSize,
                            (ii + 0.5) * GameModel.pixelSize,
                        );
                        this.container.addChild(symbol);
                        this.getLetters(indexColor).push(symbol);
                    }
                    y++;
                }
            }
            x++;
        }

        this.draw();
    }
}

class Chunk extends BufferRenderer {
    grid: extras.TilingSprite;
    letters: Letters;
    voxels = new particles.ParticleContainer(Math.pow(chunkSize, 2), {
        position: true,
    }, chunkSize ** 2, false);

    setGrid(texture: Texture) {
        this.grid = new extras.TilingSprite(texture, chunkSize * texture.width, chunkSize * texture.height);
        this.container.addChild(this.grid);
    }

    setSize(w: number, h: number): void {
        this.letters = new Letters(this.renderer, {
            width: w,
            height: h,
        });
        super.setSize(w, h);

    }
}

export class GameCanvasRenderer {
    private buffers = new Map<number, Chunk>();
    private gridVisibility = true;
    constructor(private desc: IDesc) {
        this.initPictures();
        (window as any).draw = this.draw.bind(this);
    }

    initPictures() {
        const { pixelSize, image } = this.desc;

        const xChunks = Math.ceil(this.desc.image.width / chunkSize);
        const yChunks = Math.ceil(this.desc.image.height / chunkSize);

        for (let x = 0; x < xChunks; x++) {
            for (let y = 0; y < yChunks; y++) {
                const buffer = this.getBuffer(x, y);

                let bufferWidth = pixelSize * chunkSize;
                let bufferHeight = pixelSize * chunkSize;
                if (x === xChunks - 1) bufferWidth = (image.width % chunkSize) * pixelSize;
                if (y === yChunks - 1) bufferHeight = (image.height % chunkSize) * pixelSize;

                buffer.setSize(bufferWidth, bufferHeight);
                // adds background
                // todo: добавить управление текстурными координатами и задать меньший размер для спрайта
                // (рисовать только видимую часть)
                const bg = new Sprite(image);
                bg.width = image.width * pixelSize;
                bg.height = image.height * pixelSize;
                bg.x = -x * chunkSize * pixelSize;
                bg.y = -y * chunkSize * pixelSize;
                buffer.container.addChild(bg);

                // todo: перетащить компоновку слоев в Chunk конструктор
                buffer.setGrid(this.desc.gridImage);
                buffer.letters.setLetters(this.desc.logic, image, x, y);
                buffer.container.addChild(buffer.letters.sprite);
                buffer.container.addChild(buffer.voxels);

                buffer.sprite.x = x * chunkSize * pixelSize;
                buffer.sprite.y = y * chunkSize * pixelSize;
                buffer.draw();
            }
        }


    }

    draw() {
        this.buffers.forEach(b => b.draw);
    }

    inject(parent: Container) {
        this.buffers.forEach(b => {
            parent.addChild(b.sprite);
            // console.log(b.sprite);
        });
    }

    setGridVisibility(v: boolean) {
        if (v !== this.gridVisibility) {
            this.buffers.forEach(b => {
                b.grid.visible = v;
                b.letters.sprite.visible = v;
                b.draw();
            });
            this.gridVisibility = v;
        }
    }

    showColor(index: number) {
        if (index)
            this.buffers.forEach(b => {
                b.letters.colors.forEach(color => {
                    color.forEach(letter => letter.visible = false);
                });
                b.letters.getLetters(index).forEach(l => l.visible = true);
                b.letters.draw();
                b.draw();
            });
        else
            this.buffers.forEach(b => {
                b.letters.colors.forEach(color => {
                    color.forEach(letter => letter.visible = true);
                });
                b.letters.draw();
                b.draw();
            });
    }

    drawVoxel(sprite: Sprite, x: number, y: number) {
        const xChunk = Math.floor(x / chunkSize);
        const yChunk = Math.floor(y / chunkSize)
        const buffer = this.getBuffer(xChunk, yChunk);

        buffer.voxels.addChild(sprite);
        sprite.position.set(
            (x % chunkSize) * GameModel.pixelSize,
            (y % chunkSize) * GameModel.pixelSize,
        );
        buffer.draw(sprite);
    }

    private getBufferByPixel(x: number, y: number) {
        return this.getBuffer(Math.floor(x / chunkSize), Math.floor(y / chunkSize));
    }

    private getBuffer(x: number, y: number) {
        const key = x + y * 1000;
        let buffer = this.buffers.get(key);
        if (!buffer) {
            buffer = new Chunk(this.desc.context);
            this.buffers.set(key, buffer);
        }
        return buffer;
    }
}